/**
 * STODDITY — PERSONALISATION PREVIEW WIDGET  v1.0
 *
 * Vanilla JS. Reads values live from the existing buy-buttons.liquid v3.1
 * hidden inputs so the widget never holds its own copy of the name/style.
 *
 * Fail-open: on any init error or config problem, the widget quietly removes
 * its own button so the product page looks untouched. The preview is always
 * additive — never break the page.
 */

(function () {
  'use strict';

  // ── Config from Liquid ─────────────────────────────────────────────────
  var CFG = window.STO_PREVIEW_CONFIG;
  if (!CFG || !CFG.workerUrl) {
    console.warn('[sto-preview] missing config, widget disabled');
    removeButton();
    return;
  }

  // ── DOM refs (populated on DOMContentLoaded) ──────────────────────────
  var els = {};

  // When removeButton() is called (health check returned disabled, user
  // exceeded rate limit, backend error, etc.) we set this flag to stop
  // the self-healing rescue from immediately re-inserting the wrap from
  // the template on the next sync tick.
  var widgetDisabled = false;

  // ── Boot ──────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Prestige-family quirk: the theme wraps `buy-buttons.liquid` inside a
    // `<template id="quick-buy-content">` element for use in its quick-buy
    // modal on collection pages. `<template>` children are NOT part of the
    // main DOM tree, so document.querySelector can't find them. Clone them
    // out into the main DOM the first time this runs.
    //
    // Prestige ALSO replaces the main-buy-button subtree on variant change,
    // which can strip our cloned wrap. That's handled by sync() re-running
    // this rescue whenever the wrap goes missing — see setupButtonVisibilityWatcher.
    rescueFromTemplateIfNeeded();

    try {
      // Optimistic health check so we hide the button early if the
      // backend is disabled (master switch, spend cap, etc.).
      healthCheck().then(function (enabled) {
        if (!enabled) removeButton();
      });

      // Wire events via DELEGATION on document.body so handlers survive
      // Prestige's DOM replacements. A per-element addEventListener would
      // go stale every time Prestige swaps the buy-buttons subtree.
      document.body.addEventListener('click', onDelegatedClick);
      document.addEventListener('keydown', onKeydown);
      document.addEventListener('keydown', onModalKeydown);

      setupButtonVisibilityWatcher();
    } catch (err) {
      console.warn('[sto-preview] init failed:', err);
      removeButton();
    }
  }

  /**
   * Single delegated click handler. Walks up from e.target looking for
   * one of our known action IDs / classes. Using delegation (instead of
   * attaching to the elements directly) means we don't need to re-wire
   * anything when the button/modal gets re-inserted after a Prestige
   * re-render.
   *
   * The `.sto-preview-close-trigger` class match lets any number of
   * inline Close buttons live across the different modal states — the
   * old single-ID approach didn't scale past one close button.
   */
  function onDelegatedClick(e) {
    var t = e.target;
    while (t && t !== document.body) {
      var id = t.id;
      if (id === 'sto-preview-button')          { onPreviewClick(e); return; }
      if (id === 'sto-preview-modal-backdrop')  { closeModal();      return; }
      if (id === 'sto-preview-modal-close')     { closeModal();      return; }
      if (id === 'sto-preview-error-back')      { closeModal();      return; }
      if (id === 'sto-preview-add-to-cart')     { onAddToCart();     return; }
      if (id === 'sto-preview-try-again')       { runPreview(true);  return; }
      if (id === 'sto-preview-share')           { onShareClick();    return; }
      if (t.classList && t.classList.contains('sto-preview-close-trigger')) {
        closeModal();
        return;
      }
      t = t.parentElement;
    }
  }

  /**
   * Controls button visibility based on two conditions — both must be true
   * for the "Generate a preview" button to appear:
   *
   *   (1) The selected variant is purchasable (no `.variant-unavailable` on
   *       the Add to Cart wrapper).
   *   (2) The customer has typed at least one character of personalisation.
   *
   * Important Prestige quirk: the theme REPLACES the .main-buy-button element
   * on variant change rather than mutating its class, so we watch
   * document.body instead of any specific element. We also observe the
   * personalisation editor(s) directly to catch typing + chip insertion.
   */
  function setupButtonVisibilityWatcher() {
    // Start hidden — applies to whatever wrap(s) currently exist.
    // (Prestige may replace them later; sync() takes care of re-hiding
    // newly-inserted wraps too.)
    var initialWraps = document.querySelectorAll('.sto-preview-wrap');
    for (var i = 0; i < initialWraps.length; i++) {
      initialWraps[i].classList.add('sto-pv-wrap-hidden');
    }

    function hasPersonalisationContent() {
      // Themes often duplicate the buy-buttons markup — one copy for desktop,
      // one for mobile — visibility-toggled via CSS. That means there can be
      // multiple editor elements sharing the same id. getElementById returns
      // only the first. querySelectorAll with an attribute selector returns
      // ALL of them, so we can check every copy.
      var editors = document.querySelectorAll(
        '[id="sto-editor"], [id="sto-micro-editor"]'
      );
      for (var i = 0; i < editors.length; i++) {
        var editor = editors[i];
        if (editor.textContent.trim().length > 0) return true;
        if (editor.querySelector('.sto-chip')) return true;
      }
      var hiddens = document.querySelectorAll(
        '[id="sto-personalisation-value"], [id="sto-micro-personalisation-value"]'
      );
      for (var j = 0; j < hiddens.length; j++) {
        var h = hiddens[j];
        if (h.value && h.value.trim().length > 0) return true;
      }
      return false;
    }

    function sync() {
      // If the widget has been disabled (rate-limited, spend cap, master
      // switch, etc.) bail out entirely. Without this the self-healing
      // rescue below would re-insert the wrap from the template 400ms
      // after removeButton() took it out.
      if (widgetDisabled) return;

      // Dedupe any duplicate modals that may have appeared since the last
      // tick. Prestige clones its quick-buy template into the page in
      // certain situations (observed on iPad), leaving two modals sharing
      // our IDs — which causes the loading text cycle to animate two
      // elements out of sync and visually overlap. Keep only the last one.
      var dupModals = document.querySelectorAll('[id="sto-preview-modal"]');
      if (dupModals.length > 1) {
        for (var dm = 0; dm < dupModals.length - 1; dm++) {
          dupModals[dm].remove();
        }
      }

      // Self-heal: if Prestige has stripped our wrap (e.g. after a variant
      // change re-rendered the buy-buttons subtree), re-extract it from the
      // <template id="quick-buy-content">. The cost is one querySelector
      // call per tick when everything's fine.
      var justRescued = false;
      if (!document.querySelector('.sto-preview-wrap')) {
        rescueFromTemplateIfNeeded();
        justRescued = true;
        // Ensure the fresh wrap starts hidden — the Liquid template applies
        // `sto-pv-wrap-hidden` directly on the element, but we re-apply it
        // defensively in case an older cached copy is in play.
        var freshWraps = document.querySelectorAll('.sto-preview-wrap');
        for (var fw = 0; fw < freshWraps.length; fw++) {
          freshWraps[fw].classList.add('sto-pv-wrap-hidden');
          // Force a synchronous layout flush so the browser paints the
          // hidden state before we (potentially) toggle it off in the
          // same tick. Without this reflow the browser coalesces the
          // add + remove and skips the CSS transition.
          void freshWraps[fw].offsetWidth;
        }
      }

      var atcWrapper = document.querySelector('.main-buy-button');
      var unavailable = atcWrapper && atcWrapper.classList.contains('variant-unavailable');

      var shouldShow = !unavailable && hasPersonalisationContent();
      // Re-query so we also catch wraps added after init.
      var all = document.querySelectorAll('.sto-preview-wrap');
      for (var k = 0; k < all.length; k++) {
        var wrap = all[k];
        var wasHidden = wrap.classList.contains('sto-pv-wrap-hidden');
        wrap.classList.toggle('sto-pv-wrap-hidden', !shouldShow);

        // Replay the fade-in keyframes whenever we go hidden → shown. We
        // remove then re-add the class with a forced reflow in between so
        // WebKit re-evaluates animation-name from scratch. Without this,
        // the element would pop into place instead of gently fading in.
        if (shouldShow && wasHidden) {
          wrap.classList.remove('sto-pv-wrap-just-shown');
          void wrap.offsetWidth;
          wrap.classList.add('sto-pv-wrap-just-shown');
        }
      }
    }

    // Initial state.
    sync();

    // Prestige-family themes fire this after their async variant fetch.
    document.addEventListener('variant:changed', sync);

    // Debounced sync so a burst of DOM mutations during theme re-render
    // collapses to a single check.
    var syncTimer;
    var debouncedSync = function () {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(sync, 50);
    };

    if (typeof MutationObserver !== 'undefined') {
      // Watch the whole page for variant class changes / element replacements.
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type === 'attributes') { debouncedSync(); return; }
          if (m.type === 'childList' && m.addedNodes.length) {
            debouncedSync();
            return;
          }
        }
      }).observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      // Watch ALL editor copies — themes may duplicate markup for mobile /
      // desktop views. querySelectorAll by attribute picks up duplicates that
      // getElementById misses.
      var editors = document.querySelectorAll(
        '[id="sto-editor"], [id="sto-micro-editor"]'
      );
      for (var i = 0; i < editors.length; i++) {
        var editor = editors[i];
        editor.addEventListener('input', debouncedSync);
        editor.addEventListener('keyup', debouncedSync); // iOS backup
        editor.addEventListener('blur', debouncedSync);
        new MutationObserver(debouncedSync).observe(editor, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    // Bulletproof fallback: poll every 400ms. This covers any browser quirk
    // where events or MutationObserver don't fire reliably (looking at you,
    // iOS Safari contenteditable edge cases). Cost is one DOM query every
    // 400ms, which is negligible.
    setInterval(sync, 400);
  }

  /** Focus trap — when the modal is open, Tab / Shift+Tab cycles within it. */
  function onModalKeydown(e) {
    if (e.key !== 'Tab') return;
    var modal = byId('sto-preview-modal');
    if (!modal || modal.classList.contains('sto-pv-hidden')) return;

    var focusable = Array.prototype.slice.call(
      modal.querySelectorAll('button:not([disabled])')
    ).filter(function (el) { return el.offsetParent !== null; });
    if (!focusable.length) { e.preventDefault(); return; }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── Reading live values from the existing component ──────────────────

  /**
   * Returns the current personalisation value + style, straight from the
   * buy-buttons.liquid v3.1 hidden inputs. Detects whether this is a Micro
   * product (separate editor) or a letters product.
   */
  function readCurrentState() {
    var microInput = byId('sto-micro-personalisation-value');
    var lettersInput = byId('sto-personalisation-value');
    var styleInput = byId('sto-letter-style-value');

    if (microInput) {
      // Micro products: style is always "micro", finish is always gold.
      return {
        isMicro: true,
        name: microInput.value || '',
        style: 'micro',
        finish: 'gold'
      };
    }
    if (lettersInput) {
      var rawStyle = (styleInput && styleInput.value) || 'Standard';
      return {
        isMicro: false,
        name: lettersInput.value || '',
        style: rawStyle.toLowerCase(), // Standard → standard
        finish: CFG.hardwareFinish || 'gold'
      };
    }
    return null;
  }

  /** Finds the user-visible editor so we can focus and shake it on empty. */
  function findPersonalisationField() {
    return byId('sto-micro-editor') || byId('sto-editor');
  }

  // ── Click handler ────────────────────────────────────────────────────

  function onPreviewClick(e) {
    if (e) e.preventDefault();

    var state = readCurrentState();
    if (!state) {
      console.warn('[sto-preview] no personalisation field found');
      return;
    }

    if (!state.name || state.name.trim() === '') {
      // Empty name: scroll to and shake the existing field, don't open modal.
      nudgeEmptyField();
      return;
    }

    runPreview(false);
  }

  function nudgeEmptyField() {
    var field = findPersonalisationField();
    if (!field) return;

    // Scroll into view and focus.
    var rect = field.getBoundingClientRect();
    if (rect.top < 80 || rect.bottom > window.innerHeight - 40) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(function () { try { field.focus(); } catch (e) {} }, 300);

    // Shake its container.
    var container = field.closest('.sto-letters') || field.parentElement;
    if (container) {
      container.classList.remove('sto-pv-shake');
      void container.offsetWidth; // force reflow so the animation replays
      container.classList.add('sto-pv-shake');
      setTimeout(function () { container.classList.remove('sto-pv-shake'); }, 600);
    }
  }

  // ── Modal orchestration ──────────────────────────────────────────────

  function openModal() {
    var modal = byId('sto-preview-modal');
    if (!modal) return;
    document.body.style.overflow = 'hidden';
    modal.classList.remove('sto-pv-hidden');
    modal.setAttribute('aria-hidden', 'false');
    showState('loading');
    // Force-restart the spinner animations. WebKit doesn't reliably run
    // CSS animations on elements that start inside a display:none ancestor,
    // which makes the blob stay frozen in its initial state the first time
    // the modal opens. Toggling animation-name with a reflow in between is
    // the standard way to kick them back into life.
    restartLoadingAnimations();
    setTimeout(function () {
      var close = byId('sto-preview-modal-close');
      if (close) try { close.focus(); } catch (e) {}
    }, 50);
  }

  function restartLoadingAnimations() {
    var spinner = document.querySelector('#sto-preview-loading .sto-preview-spinner');
    if (!spinner) return;
    var targets = [spinner].concat(
      Array.prototype.slice.call(spinner.querySelectorAll('.sto-pv-sub'))
    );
    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      // Disable + reflow + restore → WebKit re-evaluates animation-name
      // from scratch and begins the keyframes from 0%.
      el.style.animation = 'none';
      // Reading offsetWidth forces a synchronous layout flush.
      void el.offsetWidth;
      el.style.animation = '';
    }
  }

  function closeModal() {
    var modal = byId('sto-preview-modal');
    document.body.style.overflow = '';
    if (modal) {
      modal.classList.add('sto-pv-hidden');
      modal.setAttribute('aria-hidden', 'true');
    }
    var button = byId('sto-preview-button');
    if (button) try { button.focus(); } catch (e) {}
  }

  function showState(name) {
    toggle(byId('sto-preview-loading'), name === 'loading');
    toggle(byId('sto-preview-result'), name === 'result');
    toggle(byId('sto-preview-error'), name === 'error');

    // Only run the cycling loading messages while the loading state is visible.
    if (name === 'loading') startLoadingCycle();
    else stopLoadingCycle();
  }

  // ── Cycling loading messages ──────────────────────────────────────

  var LOADING_MESSAGES = [
    'Nice one! Initiating your preview…',
    'Sending your personalisation request…',
    'Assembling your custom personalisation…',
    'Looking good! Adding finishing touches…',
    'Almost ready, just polishing things up…'
  ];
  var loadingTimer = null;
  var loadingIdx = 0;

  // Track the pending fade setTimeout so stopLoadingCycle can cancel it.
  // Without this, a close → re-open can fire an old fade callback after
  // the new cycle has already set the first message, causing a visual
  // flicker where two messages briefly overlap.
  var loadingFadeTimer = null;

  // Updates every matching loading-text element. We use querySelectorAll
  // with an attribute selector (instead of getElementById) because Prestige
  // can leave duplicate copies in the DOM when cloning its quick-buy
  // template — only the first would be updated by getElementById, and
  // the second would keep showing an outdated message overlaid behind.
  //
  // Runs a fresh dedup pass first: if Prestige has cloned the modal mid-
  // cycle, we collapse down to a single modal before writing the text so
  // the rendered result is always consistent. This is a hot-path dedupe
  // (every 4s) in addition to the sync() dedupe (every 400ms) — belt and
  // braces for the iPad overlap issue that neither alone has cracked.
  function setAllLoadingText(text, fadeClass) {
    var modals = document.querySelectorAll('[id="sto-preview-modal"]');
    for (var m = 0; m < modals.length - 1; m++) {
      modals[m].remove();
    }

    var els = document.querySelectorAll('[id="sto-preview-loading-text"]');
    for (var i = 0; i < els.length; i++) {
      if (text !== null) els[i].textContent = text;
      if (fadeClass === 'add')    els[i].classList.add('sto-pv-fade');
      if (fadeClass === 'remove') els[i].classList.remove('sto-pv-fade');
    }
  }

  function startLoadingCycle() {
    var els = document.querySelectorAll('[id="sto-preview-loading-text"]');
    if (!els.length) return;
    stopLoadingCycle();
    loadingIdx = 0;
    setAllLoadingText(LOADING_MESSAGES[0], 'remove');

    // Cycle every 4 seconds. With 4 messages this gives ~16s of varied
    // copy to fill the typical 10–14s generation wait; if generation
    // finishes earlier, we just stop on whichever message was last. If
    // it runs longer, the cycle stops on the final message instead of
    // looping back to the start.
    loadingTimer = setInterval(function () {
      if (loadingIdx >= LOADING_MESSAGES.length - 1) {
        stopLoadingCycle();
        return;
      }
      loadingIdx++;
      // Fade out → WAIT until opacity transition has fully completed (CSS
      // is 0.18s, we wait 240ms for a 60ms paint buffer) → change text →
      // fade back in. Waiting longer than the transition guarantees old
      // and new text can never be composited together, which was the
      // root of the iPad overlap issue.
      setAllLoadingText(null, 'add');
      loadingFadeTimer = setTimeout(function () {
        setAllLoadingText(LOADING_MESSAGES[loadingIdx], 'remove');
        loadingFadeTimer = null;
      }, 240);
    }, 4000);
  }

  function stopLoadingCycle() {
    if (loadingTimer) {
      clearInterval(loadingTimer);
      loadingTimer = null;
    }
    if (loadingFadeTimer) {
      clearTimeout(loadingFadeTimer);
      loadingFadeTimer = null;
    }
  }

  function onKeydown(e) {
    if (e.key !== 'Escape') return;
    var modal = byId('sto-preview-modal');
    if (modal && !modal.classList.contains('sto-pv-hidden')) {
      closeModal();
    }
  }

  /**
   * Closes the modal and clicks the existing Add to Cart button on the page.
   *
   * We trigger a real click on the actual ATC button (not a form submit) so
   * every theme-attached behaviour fires exactly as if the customer had
   * clicked it themselves — AJAX add-to-cart, cart drawer opening, analytics
   * tracking, etc. all keep working without us reinventing them.
   */
  function onAddToCart() {
    // Scope the lookup to `.main-buy-button` specifically — the product
    // page can have other cart-add forms on it (related products,
    // upsells, cart drawer) and an unscoped `form[action*="/cart/add"]`
    // would add the wrong product to the cart.
    var atcButton =
      document.querySelector('.main-buy-button button[type="submit"]:not([disabled])') ||
      document.querySelector('buy-buttons button[type="submit"]:not([disabled])') ||
      document.querySelector('form[action*="/cart/add"] button[type="submit"]:not([disabled])');

    if (!atcButton) {
      // Probably variant-unavailable or the form's not where we expect.
      // Surface a friendly error rather than failing silently.
      console.warn('[sto-preview] could not find an enabled Add to Cart button');
      showError('We couldn\'t add this to your cart right now. Please close this and use the Add to Cart button below.');
      return;
    }

    // Click the real ATC button SYNCHRONOUSLY, in the same event-loop
    // tick as the customer's tap. iPhone Safari only treats cart-add
    // AJAX and cart-drawer side-effects as user-initiated if the click
    // happens inside the original user-gesture chain.
    atcButton.click();
    closeModal();
  }

  // ── The actual preview call ──────────────────────────────────────────

  function runPreview(skipCache) {
    var state = readCurrentState();
    if (!state || !state.name) {
      nudgeEmptyField();
      return;
    }

    openModal();
    showState('loading');

    var body = {
      shop: CFG.shop,
      product_handle: CFG.productHandle,
      product_title: CFG.productTitle,
      product_type: CFG.productType,
      product_image_url: CFG.productImageUrl,
      name: state.name,
      style: state.style,
      hardware_finish: state.finish,
      letter_layout: CFG.letterLayout || 'horizontal',
      skip_cache: !!skipCache
    };

    fetch(CFG.workerUrl + '/api/preview', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) { return { status: res.status, data: data }; });
      })
      .then(function (r) { handleResponse(r.status, r.data); })
      .catch(function (err) {
        console.warn('[sto-preview] network error:', err);
        showError('We couldn\'t reach the preview service. Please try again in a moment.');
      });
  }

  function handleResponse(status, data) {
    if (!data || typeof data !== 'object') {
      showError('We got an unexpected response. Please try again.');
      return;
    }

    // Disabled (master switch, spend cap, circuit breaker).
    if (data.enabled === false) {
      // If disabled server-side, hide the whole widget — not just show an
      // error — so the customer isn't invited to retry into a wall.
      closeModal();
      removeButton();
      return;
    }

    // Friendly errors.
    if (data.code) {
      if (data.code === 'blocked') {
        showError(data.error || 'This personalisation contains a word we can\'t put on our products. Please try a different name.');
        return;
      }
      if (data.code === 'rate_limited') {
        showError('You\'ve exceeded our daily limit of image generations. Please wait 24 hours to start generating more previews.');
        return;
      }
      if (data.code === 'invalid_input') {
        showError('Something\'s off with the personalisation. Try editing and previewing again.');
        return;
      }
      if (data.code === 'upstream_error' || data.code === 'internal_error') {
        showError('Our preview magic hit a snag. Please try again.');
        return;
      }
    }

    // Success.
    if (data.url) {
      var fullUrl = CFG.workerUrl + data.url;
      var state = readCurrentState();
      var prettyName = (state && state.name ? state.name : '').replace(/\[([A-Z ]+)\]/g, ' $1').trim();
      var altText = 'Preview of ' + CFG.productTitle + ' personalised with ' +
        (prettyName || 'your selection');

      // Preload via a fresh Image element so we capture the load event
      // reliably. Attaching handlers BEFORE setting src is critical: if the
      // image is already in the browser cache, setting src fires onload
      // synchronously, which must happen after the listener is wired up.
      var loader = new Image();
      var settled = false;
      var timeout = setTimeout(function () {
        if (settled) return;
        settled = true;
        showError('The preview image took too long to load. Please try again.');
      }, 30000);

      loader.onload = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        var img = byId('sto-preview-image');
        if (img) {
          img.alt = altText;
          img.src = loader.src;
        }
        showState('result');
      };
      loader.onerror = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        showError('The preview image failed to load. Please try again.');
      };
      loader.src = fullUrl;
      return;
    }

    showError('We couldn\'t create a preview this time. Please try again.');
  }

  function showError(message) {
    var em = byId('sto-preview-error-message');
    if (em) em.textContent = message;
    showState('error');
  }

  // ── Share button ─────────────────────────────────────────────────────

  /**
   * Share the current preview. Uses the native Web Share API on mobile so
   * the customer gets their OS share sheet (iMessage, WhatsApp, Instagram,
   * email, etc.) and the preview image is included as an attached file
   * where the platform supports it. Falls back to copying the product URL
   * to the clipboard on desktop browsers that don't implement Web Share.
   */
  function onShareClick() {
    var imgEl = byId('sto-preview-image');
    var imageUrl = imgEl ? imgEl.src : '';
    // Share the product page URL so recipients who click land somewhere
    // useful. window.location.origin gives the canonical host (stoddity.com
    // in production, shopifypreview.com in preview mode).
    var productUrl = window.location.origin + '/products/' + CFG.productHandle;
    // Keep the shared text generic (no personalisation name). Many customers
    // are sharing the preview with the gift recipient, so showing that
    // person's name back to them would feel weird.
    var title = 'My St. Oddity preview';
    var text = 'Check out my personalised ' + CFG.productTitle;

    var baseShareData = { title: title, text: text, url: productUrl };

    if (!navigator.share) {
      // No Web Share API — desktop fallback: copy the IMAGE url to the
      // clipboard so the customer can paste the preview directly
      // anywhere (email, DM, etc.) rather than just a product link.
      copyLinkAndFlash(imageUrl || productUrl);
      return;
    }

    // Try to include the preview image as a file, then fall back to
    // text+url-only share if the platform doesn't support files.
    shareWithImage(imageUrl, baseShareData).catch(function (err) {
      // AbortError = user cancelled the share sheet. Not an error we
      // need to react to; just silently return.
      if (err && err.name === 'AbortError') return;
      // Any other error — fall back to copy the image URL.
      copyLinkAndFlash(imageUrl || productUrl);
    });
  }

  function shareWithImage(imageUrl, baseShareData) {
    if (!imageUrl) {
      return navigator.share(baseShareData);
    }
    return fetch(imageUrl)
      .then(function (res) { return res.blob(); })
      .then(function (blob) {
        var ext = (blob.type || 'image/webp').split('/')[1] || 'webp';
        var file = new File([blob], 'stoddity-preview.' + ext, { type: blob.type });
        var withFile = {
          title: baseShareData.title,
          text: baseShareData.text,
          url: baseShareData.url,
          files: [file],
        };
        if (navigator.canShare && navigator.canShare(withFile)) {
          return navigator.share(withFile);
        }
        return navigator.share(baseShareData);
      })
      .catch(function (err) {
        // Image fetch failed (e.g. CORS), share without the file.
        if (err && err.name === 'AbortError') throw err; // let caller handle
        return navigator.share(baseShareData);
      });
  }

  function copyLinkAndFlash(url) {
    var write = navigator.clipboard && navigator.clipboard.writeText
      ? navigator.clipboard.writeText(url)
      : Promise.reject();

    write.then(function () {
      flashShareButton('Link copied');
    }, function () {
      // Clipboard API not available or blocked — try the legacy
      // document.execCommand fallback.
      try {
        var ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flashShareButton('Link copied');
      } catch (err) {
        flashShareButton('Could not share');
      }
    });
  }

  function flashShareButton(message) {
    var btn = byId('sto-preview-share');
    if (!btn) return;
    var label = btn.querySelector('.sto-preview-share__label');
    if (!label) return;
    var original = label.textContent;
    label.textContent = message;
    btn.classList.add('sto-preview-pill--confirmed');
    setTimeout(function () {
      label.textContent = original;
      btn.classList.remove('sto-preview-pill--confirmed');
    }, 1800);
  }

  // ── Lightweight health check (GET /health) ──────────────────────────

  function healthCheck() {
    // 2-second timeout so a slow backend doesn't leave the button flashing
    // visible for many seconds before being removed. Matches the plan's
    // Layer 4 frontend fail-open contract.
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 2000) : null;

    return fetch(CFG.workerUrl + '/health', {
      method: 'GET',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (timer) clearTimeout(timer);
        // data is { ok: true, enabled: true/false, reason: ... }
        return !!(data && data.enabled);
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        // On any error (including timeout), hide the button. Fail-closed.
        return false;
      });
  }

  // ── Rescue wrap + modal from <template> elements ─────────────────────

  /**
   * Clones the wrap + modal out of any <template> that contains them and
   * inserts them into the main DOM. This is a WORKAROUND for Prestige-family
   * themes where `snippets/buy-buttons.liquid` is only included inside a
   * `<template id="quick-buy-content">` for the collection-page quick-buy
   * modal — leaving our markup un-queryable on real product pages.
   *
   * Safe no-op if the wrap already exists in the main DOM.
   */
  function rescueFromTemplateIfNeeded() {
    if (widgetDisabled) return;
    try {
      // De-dupe: Prestige sometimes clones the quick-buy template into the
      // page, which can leave multiple elements sharing our IDs. Keep only
      // the last instance of the modal (the most recently inserted one) so
      // the loading cycle doesn't animate two elements out of sync.
      var existingModals = document.querySelectorAll('[id="sto-preview-modal"]');
      for (var m = 0; m < existingModals.length - 1; m++) {
        existingModals[m].remove();
      }

      var wrapInDom = !!document.querySelector('.sto-preview-wrap');
      var modalInDom = !!document.getElementById('sto-preview-modal');
      var stylesInHead = !!document.getElementById('sto-preview-styles');

      if (wrapInDom && modalInDom) return;

      // Find any template whose content contains our wrap.
      var templates = document.getElementsByTagName('template');
      var sourceTemplate = null;
      for (var i = 0; i < templates.length; i++) {
        var tpl = templates[i];
        if (tpl.content && tpl.content.querySelector('.sto-preview-wrap')) {
          sourceTemplate = tpl;
          break;
        }
      }
      if (!sourceTemplate) return;

      var wrapSrc = sourceTemplate.content.querySelector('.sto-preview-wrap');
      var modalSrc = sourceTemplate.content.querySelector('#sto-preview-modal');
      var styleSrc = sourceTemplate.content.querySelector('style');

      // Choose insertion point: before the visible Add to Cart wrapper.
      var atcWrapper = document.querySelector('.main-buy-button');
      var insertionPoint = null;
      var insertionParent = null;

      if (atcWrapper && atcWrapper.parentElement) {
        insertionPoint = atcWrapper;
        insertionParent = atcWrapper.parentElement;
      } else {
        var form = document.querySelector('form[action*="/cart/add"]');
        if (form) insertionParent = form;
      }

      if (!wrapInDom && wrapSrc && insertionParent) {
        var wrapClone = wrapSrc.cloneNode(true);
        if (insertionPoint) {
          insertionParent.insertBefore(wrapClone, insertionPoint);
        } else {
          insertionParent.appendChild(wrapClone);
        }
      }

      // Clone style block into <head> so the cloned wrap actually looks styled.
      if (!stylesInHead && styleSrc) {
        var styleClone = styleSrc.cloneNode(true);
        styleClone.id = 'sto-preview-styles';
        document.head.appendChild(styleClone);
      }

      // Modal goes on document.body.
      if (!modalInDom && modalSrc) {
        document.body.appendChild(modalSrc.cloneNode(true));
      }
    } catch (err) {
      console.warn('[sto-preview] rescueFromTemplateIfNeeded failed:', err);
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  function byId(id) { return document.getElementById(id); }
  function toggle(el, show) {
    if (!el) return;
    el.classList.toggle('sto-pv-hidden', !show);
  }
  function removeButton() {
    widgetDisabled = true;
    // Remove every instance, not just the first — duplicates may exist
    // if Prestige has cloned the quick-buy template into the page.
    var wraps = document.querySelectorAll('.sto-preview-wrap');
    for (var i = 0; i < wraps.length; i++) wraps[i].remove();
    var modals = document.querySelectorAll('[id="sto-preview-modal"]');
    for (var j = 0; j < modals.length; j++) modals[j].remove();
  }
})();
